import { Renderer } from '@dfares/renderer';
import { CursorState, ModalManagerEvent, Setting } from '@dfares/types';
// import * as fabric from 'fabric'; // v6
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useUIManager } from '../Utils/AppHooks';
import UIEmitter, { UIEmitterEvent } from '../Utils/UIEmitter';
import Viewport from './Viewport';

const CanvasWrapper = styled.div`
  width: 100%;
  height: 100%;

  position: relative;

  canvas {
    width: 100%;
    height: 100%;

    position: absolute;

    &#buffer {
      width: auto;
      height: auto;
      display: none;
    }
  }
  // TODO put this into a global style
  canvas,
  img {
    image-rendering: -moz-crisp-edges;
    image-rendering: -webkit-crisp-edges;
    image-rendering: pixelated;
    image-rendering: crisp-edges;
  }
`;

const syncCanvasBackingSize = (
  source: HTMLCanvasElement,
  canvases: HTMLCanvasElement[]
): { width: number; height: number } | undefined => {
  const width = source.clientWidth;
  const height = source.clientHeight;
  if (!(width > 0) || !(height > 0)) return undefined;

  canvases.forEach((canvas) => {
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
  });

  return { width, height };
};

export default function ControllableCanvas() {
  // html canvas element width and height. viewport dimensions are tracked by viewport obj
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);
  const [canvasReady, setCanvasReady] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const glRef = useRef<HTMLCanvasElement | null>(null);
  const bufferRef = useRef<HTMLCanvasElement | null>(null);

  const evtRef = canvasRef;

  // const [fCanvas, setFCanvas] = useState<fabric.Canvas | null>(null);

  const gameUIManager = useUIManager();

  const modalManager = gameUIManager.getModalManager();
  const [targeting, setTargeting] = useState<boolean>(false);

  useEffect(() => {
    const updateTargeting = (newstate: CursorState) => {
      setTargeting(newstate === CursorState.TargetingExplorer);
    };
    modalManager.on(ModalManagerEvent.StateChanged, updateTargeting);
    return () => {
      modalManager.removeListener(ModalManagerEvent.StateChanged, updateTargeting);
    };
  }, [modalManager]);

  const doResize = useCallback(() => {
    if (canvasRef.current) {
      const canvasWidth = canvasRef.current.clientWidth;
      const canvasHeight = canvasRef.current.clientHeight;
      setWidth(canvasWidth);
      setHeight(canvasHeight);
      setCanvasReady((isReady) => isReady || (canvasWidth > 0 && canvasHeight > 0));
    }
  }, [canvasRef]);

  useLayoutEffect(() => {
    doResize();
  }, [doResize]);

  // Re-read the canvas size and observe future layout changes (e.g. when the
  // game window first becomes visible) so the viewport always picks up real
  // dimensions without requiring manual interaction.
  useEffect(() => {
    const target = canvasRef.current;
    if (!target || typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(() => doResize());
    resizeObserver.observe(target);

    return () => resizeObserver.disconnect();
  }, [doResize, canvasRef]);

  // Emit WindowResize only after React has committed the new width/height onto
  // the canvas element, so Viewport.onWindowResize reads the up-to-date
  // canvas.width/height (avoids the blank-map-until-scroll race).
  useEffect(() => {
    if (width > 0 && height > 0) {
      UIEmitter.getInstance().emit(UIEmitterEvent.WindowResize);
    }
  }, [width, height]);

  useEffect(() => {
    if (!gameUIManager || !canvasReady) return;
    // if (!fCanvas && canvasRef.current) {
    //   // setFCanvas(new fabric.Canvas(canvasRef.current));
    // }

    // const canvas = fCanvas?.getSelectionElement();
    const canvas = evtRef.current;
    if (!canvas || !canvasRef.current || !glRef.current || !bufferRef.current) return;

    const canvasSize = syncCanvasBackingSize(canvas, [canvasRef.current, glRef.current]);
    if (!canvasSize) {
      doResize();
      return;
    }

    setWidth(canvasSize.width);
    setHeight(canvasSize.height);

    const uiEmitter: UIEmitter = UIEmitter.getInstance();

    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const { deltaY } = e;
      uiEmitter.emit(UIEmitterEvent.CanvasScroll, deltaY);
    };

    // This zooms your home world in really close to show the awesome details
    // TODO: Store this as it changes and re-initialize to that if stored
    const defaultWorldUnits = 4;
    Viewport.initialize(gameUIManager, defaultWorldUnits, canvas);
    Renderer.initialize(
      canvasRef.current,
      glRef.current,
      bufferRef.current,
      Viewport.getInstance(),
      gameUIManager,
      {
        spaceColors: {
          innerNebulaColor: gameUIManager.getStringSetting(Setting.RendererColorInnerNebula),
          nebulaColor: gameUIManager.getStringSetting(Setting.RendererColorNebula),
          spaceColor: gameUIManager.getStringSetting(Setting.RendererColorSpace),
          deepSpaceColor: gameUIManager.getStringSetting(Setting.RendererColorDeepSpace),
          deadSpaceColor: gameUIManager.getStringSetting(Setting.RendererColorDeadSpace),
        },
      }
    );
    // We can't attach the wheel event onto the canvas due to:
    // https://www.chromestatus.com/features/6662647093133312
    canvas.addEventListener('wheel', onWheel);
    // fCanvas.on("mouse:wheel", onWheel);
    window.addEventListener('resize', doResize);

    uiEmitter.on(UIEmitterEvent.UIChange, doResize);

    return () => {
      Viewport.destroyInstance();
      Renderer.destroy();
      canvas.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', doResize);
      uiEmitter.removeListener(UIEmitterEvent.UIChange, doResize);
    };
  }, [gameUIManager, canvasReady, doResize, canvasRef, glRef, bufferRef, evtRef]);

  // attach event listeners
  useEffect(() => {
    if (!evtRef.current) return;
    const canvas = evtRef.current;

    const uiEmitter: UIEmitter = UIEmitter.getInstance();

    function onMouseEvent(emitEventName: UIEmitterEvent, mouseEvent: MouseEvent) {
      const rect = canvas.getBoundingClientRect();
      const canvasX = mouseEvent.clientX - rect.left;
      const canvasY = mouseEvent.clientY - rect.top;
      uiEmitter.emit(emitEventName, { x: canvasX, y: canvasY });
    }

    const onMouseDown = (e: MouseEvent) => {
      onMouseEvent(UIEmitterEvent.CanvasMouseDown, e);
    };
    // this is the root of the mousemove event
    const onMouseMove = (e: MouseEvent) => {
      onMouseEvent(UIEmitterEvent.CanvasMouseMove, e);
    };
    const onMouseUp = (e: MouseEvent) => {
      onMouseEvent(UIEmitterEvent.CanvasMouseUp, e);
    };
    // TODO convert this to mouseleave
    const onMouseOut = () => {
      uiEmitter.emit(UIEmitterEvent.CanvasMouseOut);
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseout', onMouseOut);
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseout', onMouseOut);
    };
  }, [evtRef]);

  return (
    <CanvasWrapper style={{ cursor: targeting ? 'crosshair' : undefined }}>
      <canvas ref={glRef} width={width} height={height} />
      <canvas ref={canvasRef} width={width} height={height} />
      <canvas ref={bufferRef} id='buffer' />
    </CanvasWrapper>
  );
}
